import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useAuthStore } from "../../store/useAuthStore";
import { getAuthMe } from "../../api/auth.api";

export default function AuthCallback() {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const updateUser = useAuthStore((s) => s.updateUser);
  const token = useAuthStore((s) => s.token);
  const [status, setStatus] = useState<"loading" | "error">("loading");

  useEffect(() => {
    // Already logged in → check roles and redirect
    if (token) {
      handleRedirectBasedOnRole();
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const rawUserInfo = params.get("user_info");

    if (!rawUserInfo) {
      console.warn("No user info found in URL, redirecting...");
      setStatus("error");
      setTimeout(
        () => navigate({ to: "/", search: { section: undefined } }),
        500,
      );
      return;
    }

    try {
      const userInfo = JSON.parse(
        decodeURIComponent(decodeURIComponent(rawUserInfo)),
      );
      const { access_token, user, refresh_token } = userInfo;

      if (!access_token || !user) throw new Error("Invalid login data");

      login({
        user: {
          id: user?.id || "",
          email: user?.email || "",
          username: user?.username || null,
          profile_picture: user?.picture || "",
          first_name: user?.first_name || "",
          last_name: user?.last_name || "",
          mongo_id: user?.mongo_id || "",
        },
        token: access_token,
        refresh_token: refresh_token,
      });

      // Fetch user roles and redirect accordingly
      handleRedirectBasedOnRole();
    } catch (err) {
      console.error("Login callback failed", err);
      setStatus("error");
      setTimeout(
        () => navigate({ to: "/", search: { section: undefined } }),
        500,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [login, token, navigate]);

  const handleRedirectBasedOnRole = async () => {
    try {
      const userDetails = await getAuthMe();

      // Update user with role information
      updateUser({
        role_name: userDetails.role_name,
      });

      // Role-based redirection
      const isAdmin = userDetails.role_name.toLowerCase() === "admin";

      const isManager = userDetails.role_name.toLowerCase() === "manager";

      // Set status BEFORE navigation to prevent route guard issues
      const setAdminStatus = useAuthStore.getState().setAdminStatus;
      const setManagerStatus = useAuthStore.getState().setManagerStatus;
      setAdminStatus(isAdmin);
      setManagerStatus(isManager);

      setTimeout(() => {
        window.history.replaceState({}, document.title, "/");
        if (isAdmin) {
          navigate({ to: "/admin/dashboard" });
        } else if (isManager) {
          navigate({ to: "/manager/subscription-management" });
        } else {
          navigate({ to: "/", search: { section: undefined } });
        }
      }, 50);
    } catch (error) {
      console.error("Failed to fetch user details:", error);
      // Set as non-admin/non-manager on error
      const setAdminStatus = useAuthStore.getState().setAdminStatus;
      const setManagerStatus = useAuthStore.getState().setManagerStatus;
      setAdminStatus(false);
      setManagerStatus(false);
      // Fallback to regular user dashboard
      setTimeout(() => {
        window.history.replaceState({}, document.title, "/");
        navigate({ to: "/", search: { section: undefined } });
      }, 50);
    }
  };

  if (status === "error") navigate({ to: "/", search: { section: undefined } });
  return <p>🔄 Signing you in…</p>;
}
